import { unpackRules } from '@casl/ability/extra'
import * as Sentry from '@sentry/nextjs'
import { getAuth, signOut } from 'firebase/auth'
import { createMachine, assign, DoneInvokeEvent, Interpreter } from 'xstate'
import { MeQuery } from 'types/graphql'
import { deleteAuthCookies } from 'api/authCookies'
import { resizedImageUrl } from 'utils/imageUrl'
import { deleteKnockCookie } from 'modules/notifications/NotificationsProvider'
import { createUnauthenticatedAbility, AppAbility } from '../abilities'
import { MaybeMe } from '../queries/useServerSideMe'

type SessionContext = {
  user?: MeQuery['me'] | null
  ability: AppAbility
}

type LoggedInSessionContext = {
  user: MeQuery['me']
  ability: AppAbility
}

type LoggedOutSessionContext = {
  user: null
  ability: AppAbility
}

type FetchUserAction = { type: 'FETCH_USER' }
type SetUserAction = { type: 'SET_USER'; user: MeQuery['me'] }
type SetNoUserAction = { type: 'SET_NO_USER' }
type UpdateUserAction = { type: 'UPDATE_USER'; user: MeQuery['me'] }

type SessionAction = FetchUserAction | SetUserAction | SetNoUserAction | UpdateUserAction

type SessionState =
  | {
      value: 'initializing'
      context: LoggedOutSessionContext
    }
  | {
      value: 'fetchingUser'
      context: LoggedOutSessionContext
    }
  | {
      value: 'loggedIn'
      context: LoggedInSessionContext
    }
  | {
      value: 'unidentified'
      context: LoggedOutSessionContext
    }

type DoneFetchedUser = DoneInvokeEvent<MeQuery['me']>

export type SessionService = Interpreter<SessionContext, any, SessionAction, SessionState>

export const sessionMachine = (user?: MaybeMe) => {
  /**
   * Default initial state & context to assume the user has not been logged in server-side
   */
  let initial = 'initializing'
  let context: LoggedOutSessionContext | LoggedInSessionContext = {
    user: null,
    ability: createUnauthenticatedAbility()
  }

  /**
   * If the user was logged in server-side, `user` will be present, so we can start the machine
   * in the `loggedIn` state, and manually set the context to have what it needs
   */
  if (user) {
    initial = 'loggedIn'
    context = {
      user,
      ability: context.ability.update(unpackRules<any>(user.abilities)) as AppAbility
    }
  }

  return createMachine<SessionContext, SessionAction, SessionState>(
    {
      id: 'sessionMachine',
      initial,
      context,
      states: {
        initializing: {
          on: {
            FETCH_USER: {
              target: 'fetchingUser'
            },
            SET_NO_USER: {
              target: 'unidentified'
            }
          }
        },
        unidentified: {
          on: {
            FETCH_USER: {
              target: 'fetchingUser'
            }
          }
        },
        fetchingUser: {
          invoke: {
            src: 'getCurrentUser',
            onDone: {
              target: 'loggedIn',
              actions: ['setCurrentUser']
            },
            onError: {
              target: 'unidentified',
              actions: ['unsetCurrentUser', 'resetUserTracking', 'logFetchUserError', 'forceSignOut']
            }
          }
        },
        loggedIn: {
          entry: ['setupUserTracking'],
          on: {
            UPDATE_USER: {
              actions: ['updateUser', 'setupUserTracking']
            },
            SET_NO_USER: {
              target: 'unidentified',
              actions: ['unsetCurrentUser', 'resetUserTracking']
            }
          }
        }
      }
    },
    {
      actions: {
        setCurrentUser: assign<SessionContext, DoneFetchedUser>(({ ability }, { data: user }) => ({
          user,
          ability: ability.update(unpackRules<any>(user.abilities)) as AppAbility
        })) as any,
        updateUser: assign<SessionContext, UpdateUserAction>(({ ability }, { user }) => ({
          user,
          ability: ability.update(unpackRules<any>(user.abilities)) as AppAbility
        })) as any,
        unsetCurrentUser: assign<SessionContext, DoneInvokeEvent<null>>(({ ability }) => ({
          user: null,
          ability: createUnauthenticatedAbility()
        })) as any,
        setupUserTracking: ({ user }: SessionContext) => {
          window.analytics.identify(user.firebaseUid, {
            name: user.fullName,
            email: user.email,
            role: user.role,
            membershipLevel: user.membershipLevel,
            betaTester: user.betaTester,
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            avatar: user.avatarFullPath ? resizedImageUrl(user.avatarFullPath, { width: 500, height: 500 }) : null,
            createdAt: user.createdAt,
            birthday: user.personal.birthday,
            hasKids: user.personal.hasKids,
            hasPets: user.personal.hasPets,
            pronoun: user.personal.pronoun,
            level: user.level
          })
          Sentry.configureScope((scope) => {
            scope.setUser({ id: user.firebaseUid, email: user.email, role: user.role })
          })
        },
        resetUserTracking: () => {
          window.analytics.reset()
          Sentry.configureScope((scope) => {
            scope.setUser(null)
          })
        },
        logFetchUserError: () => {
          Sentry.withScope((scope) => {
            const auth = getAuth()
            scope.setUser({ id: auth.currentUser.uid, email: auth.currentUser.email })
            Sentry.captureException(new Error('Failed to fetch user even though firebase user exists'))
          })
        },
        forceSignOut: () => {
          const auth = getAuth()
          signOut(auth).then(() => {
            deleteAuthCookies()
            deleteKnockCookie()
          })
        }
      }
    }
  )
}
